import { ascensionSkillPointGain } from '../ability/skill-roll.js';
import { casterSkills } from '../pl/passes.js';
import { trapSkillFor } from './skill-check.js';
import { paoeEntryContext } from '../ability/paoe-entry.js';
import { ecId, handBack, record, type JanusReaders, type LogRow, type Row } from '../ability/context.js';
import { payloadDryRunRollSource } from '../ability/environmental-payload.js';
import type { RollSource } from '../actions/roll-source.js';
import type { DisarmVerdict } from './board-validators.js';
import { planTrapLifecycle, type TrapLifecycle } from './lifecycle.js';
import type { TrapCheckSave } from './skill-check.js';

export interface DisarmOutcomePlan {
  save: Pick<TrapCheckSave, 'rows' | 'playerCharactersChanged'>;
  lifecycle: TrapLifecycle;
}

/** A perfect board earns a synthetic natural 20, with only the native ascension confirmation drawn. */
export function planDisarmOutcome(input: { encounter: Row; actor: Row; host: Row; members: ReadonlyMap<string, Row>;
  read: JanusReaders; skill: 'machining' | 'magic'; outcome: DisarmVerdict }, roll: RollSource): DisarmOutcomePlan {
  const { encounter, actor, host, members, read, skill, outcome } = input;
  const lifecycle = outcome.success ? planTrapLifecycle(host, encounter, members, {}, read) : { removals: [], skipped: [] };
  const perform = (source: RollSource): DisarmOutcomePlan['save'] => {
    const ctx = paoeEntryContext(encounter, members, read, source);
    const roller = ctx.members.get(ecId(actor));
    if (!roller) handBack('trap-disarm:actor-not-held');
    const tier = Number(record(casterSkills(roller)[skill])?.tier_level ?? 0);
    if (outcome.success && outcome.perfect && tier > 0) ascensionSkillPointGain(ctx, roller,
      { id: trapSkillFor(skill === 'magic' ? 'magical' : 'mechanical'), tier }, 20, skill);
    for (const row of ctx.rows) row.roll_private = 0;
    const plan = { rows: ctx.rows, playerCharactersChanged: ctx.playerCharactersChanged ?? [] };
    if (!outcome.sprung) plan.rows.unshift({ roll_type_1: outcome.success ? 'trap_disarmed' : 'trap_disarm_failed',
      roll_type_2: '', roll_item_id: String(read.inventoryField?.(host, 'inventory_id') ?? ''),
      roll_by: ecId(actor),
      roll_to: String(read.inventoryField?.(host, 'inventory_id') ?? ''), roll_result: 0 } satisfies LogRow);
    return plan;
  };
  perform(payloadDryRunRollSource('min'));
  perform(payloadDryRunRollSource('max'));
  return { save: perform(roll), lifecycle };
}
